import { useState, useMemo, useCallback, useEffect } from "react";
import WidgetContainer from "../reusablesComponents/containerWidget";
import GroupsPanel from "./groupStage/groups/groupsPanel";
import MatchesPanel from "./groupStage/matches/matchesPanel";
import QualifyingRoundsPanel from "./qualifyingRounds/qualifyingRoundsPanel";
import { computeStandings, simulateGroupStage, simulateSingleGroup, areAllGroupsComplete, TABS, generateQualifyingMatchesFromGroups, initializeGroupSimulationState, simulateQualifyingRounds, getOfficialResult, loadOfficialResults } from "./functionsSimulator";
import Actions from "./groupStage/actions/actions";
import { groupsDataUpdated } from "./groupsDataUpdated";
import "./simulatorWorldCup.sass";

// Inicializar todos los grupos con simulationState
const initializeGroupsState = (groups) => {
  const initialized = {};
  Object.keys(groups).forEach((groupId) => {
    initialized[groupId] = initializeGroupSimulationState(groups[groupId]);
  });
  return initialized;
};

export default function SimulatorWorldCup() {
  const [activeGroup, setActiveGroup] = useState("A");
  const [groupsState, setGroupsState] = useState({});
  const [phase, setPhase] = useState("groups");
  const [qualifyingState, setQualifyingState] = useState({});
  const [shouldUpdateQualifying, setShouldUpdateQualifying] = useState(false);
  const [scrollToChampion, setScrollToChampion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasAutoSwitchedToQualifying, setHasAutoSwitchedToQualifying] = useState(false);

  // Cargar resultados oficiales antes de inicializar grupos
  useEffect(() => {
    loadOfficialResults().then(() => {
      setGroupsState(initializeGroupsState(groupsDataUpdated));
      setLoading(false);
    });
  }, []);

  const onChangeScore = (groupId, matchId, field, value) => {
    const clean = value === "" ? "" : String(Math.max(0, Math.min(99, Number(value))));

    setGroupsState((prev) => {
      const group = prev[groupId];
      if (!group) return prev;

      const nextDays = group.days.map((d) => ({
        ...d,
        matches: d.matches.map((m) => {
          if (m.id === matchId && !m.locked) return { ...m, [field]: clean, simulated: false };
          return m;
        }),
      }));

      return { ...prev, [groupId]: { ...group, days: nextDays } };
    });
  };

  const standings = useMemo(() => {
    const g = groupsState[activeGroup];
    return g ? computeStandings(g, activeGroup) : [];
  }, [groupsState, activeGroup]);

  const isGroupsComplete = useMemo(() => {
    return areAllGroupsComplete(groupsState, TABS);
  }, [groupsState]);

  // Sincronizar qualifying cuando cambia groupsState y shouldUpdateQualifying es true
  useEffect(() => {
    if (shouldUpdateQualifying && phase === "qualifying") {
      setQualifyingState(generateQualifyingMatchesFromGroups(groupsState));
      setShouldUpdateQualifying(false);
    }
  }, [shouldUpdateQualifying, phase, groupsState]);

  // Cambiar automáticamente a eliminatorias cuando todos los grupos estén completos (solo la primera vez)
  useEffect(() => {
    if (isGroupsComplete && phase === "groups" && !hasAutoSwitchedToQualifying) {
      setPhase("qualifying");
      setQualifyingState(generateQualifyingMatchesFromGroups(groupsState));
      setHasAutoSwitchedToQualifying(true);
    }
  }, [isGroupsComplete]);

  // Función para simular todos los partidos de fase de grupos
  const handleSimulate = () => {
    const simulatedGroups = simulateGroupStage(groupsState);
    setGroupsState(simulatedGroups);
    setShouldUpdateQualifying(true);
    setPhase("qualifying");
    setQualifyingState(generateQualifyingMatchesFromGroups(simulatedGroups));
  };

  // Función para simular solo un grupo específico
  const handleSimulateGroup = (groupId, group) => {
    const simulatedGroup = simulateSingleGroup(group);
    setGroupsState((prev) => ({
      ...prev,
      [groupId]: simulatedGroup,
    }));
    setShouldUpdateQualifying(true);
  };

  // Función para simular todas las eliminatorias sin afectar grupos
  const handleSimulateQualifying = () => {
    setQualifyingState((prev) => simulateQualifyingRounds(prev));
    setScrollToChampion(true);
  };

  // Función para reiniciar todo
  const handleReset = () => {
    const initializedGroups = initializeGroupsState(groupsDataUpdated);
    setGroupsState(initializedGroups);
    setQualifyingState(generateQualifyingMatchesFromGroups(initializedGroups));
    setPhase("qualifying");
    setActiveGroup("A");
    setShouldUpdateQualifying(false);
    setHasAutoSwitchedToQualifying(false);
  };

  const handlePhaseChange = (newPhase) => {
    setPhase(newPhase);
    if (newPhase === "qualifying") {
      setQualifyingState(generateQualifyingMatchesFromGroups(groupsState));
    }
  };

  const handleSelectWinner = useCallback((matchId, winner) => {
    setQualifyingState((prev) => {
      const updated = { ...prev };

      const findMatchInfo = () => {
        const rounds = ['roundOf32', 'roundOf16', 'roundOf8', 'semiFinals', 'final', 'thirdPlace'];
        for (const roundKey of rounds) {
          const match = prev[roundKey]?.find(m => m.id === matchId);
          if (match) return { roundKey, match, previousWinner: match.winner };
        }
        return { roundKey: null, match: null, previousWinner: null };
      };

      const { match: targetMatch, roundKey: changedRound, previousWinner } = findMatchInfo();
      if (targetMatch?.locked) return prev;

      // Helper para limpiar ganador en cascada si el equipo eliminado estaba seleccionado
      const clearWinnerInCascade = (roundKey, eliminatedTeam) => {
        if (!eliminatedTeam || !updated[roundKey]) return;
        updated[roundKey] = updated[roundKey].map((m) => {
          // No tocar partidos con resultado oficial
          if (m.locked) return m;
          // Si el equipo eliminado era el ganador de este partido, limpiar
          if (m.winner === eliminatedTeam) {
            return { ...m, winner: null };
          }
          return m;
        });
      };

      // Determinar qué equipo fue "eliminado" (el que ya no avanza)
      const getEliminatedTeam = () => {
        if (!previousWinner) return null;
        // Si había un ganador previo y ahora es diferente, el anterior fue eliminado
        if (previousWinner !== winner) return previousWinner;
        return null;
      };

      const eliminatedTeam = getEliminatedTeam();

      // Limpiar en cascada las rondas posteriores si hay un equipo eliminado
      if (eliminatedTeam) {
        const roundOrder = ['roundOf32', 'roundOf16', 'roundOf8', 'semiFinals', 'thirdPlace', 'final'];
        const changedRoundIndex = roundOrder.indexOf(changedRound);
        // Limpiar desde la ronda siguiente en adelante
        for (let i = changedRoundIndex + 1; i < roundOrder.length; i++) {
          clearWinnerInCascade(roundOrder[i], eliminatedTeam);
        }
      }

      // Helper para actualizar el ganador en una ronda
      const updateRound = (roundKey) => {
        if (prev[roundKey]) {
          updated[roundKey] = updated[roundKey].map((m) =>
            m.id === matchId && !m.locked ? { ...m, winner } : m
          );
        }
      };

      // Actualizar el ganador en la ronda correspondiente
      updateRound('roundOf32');
      updateRound('roundOf16');
      updateRound('roundOf8');
      updateRound('semiFinals');
      updateRound('final');
      updateRound('thirdPlace');

      // Helper para obtener ganador o referencia al partido
      const getWinnerOrRef = (match) => {
        if (!match) return "TBD";
        if (match.winner) return match.winner;
        return `GP${match.matchNumber}`;
      };

      const getLoserOrRef = (match) => {
        if (!match) return "TBD";
        if (match.winner) return match.winner === match.home ? match.away : match.home;
        return `PP${match.matchNumber}`;
      };

      // Helper para preservar resultado oficial o usar valor existente
      // const getOfficialOrExisting = (matchNumber, existingMatch) => {
      //   const official = getOfficialResult(matchNumber);
      //   if (official) return { winner: official.winner || null, homeScore: official.homeScore ?? null, awayScore: official.awayScore ?? null, locked: true };
      //   return { winner: existingMatch?.winner || null, homeScore: existingMatch?.homeScore ?? null, awayScore: existingMatch?.awayScore ?? null, locked: false };
      // };

      // Helper que resetea winner/scores si los participantes del partido cambiaron
      const getOfficialOrReset = (matchNumber, existingMatch, newHome, newAway) => {
        const official = getOfficialResult(matchNumber);
        if (official) return { winner: official.winner || null, homeScore: official.homeScore ?? null, awayScore: official.awayScore ?? null, locked: true };
        // Si los participantes cambiaron, limpiar winner y scores
        if (existingMatch && (existingMatch.home !== newHome || existingMatch.away !== newAway)) {
          return { winner: null, homeScore: null, awayScore: null, locked: false };
        }
        return { winner: existingMatch?.winner || null, homeScore: existingMatch?.homeScore ?? null, awayScore: existingMatch?.awayScore ?? null, locked: false };
      };

      // Update Octavos desde Dieciseisavos (basado en números de partido)
      if (updated.roundOf32 && updated.roundOf32.length >= 16) {
        const getR32Match = (matchNum) => updated.roundOf32.find(m => m.matchNumber === matchNum);

        const r16Data = [
          { idx: 0, id: "r16-89", mn: 89, h: getWinnerOrRef(getR32Match(74)), a: getWinnerOrRef(getR32Match(77)) },
          { idx: 1, id: "r16-90", mn: 90, h: getWinnerOrRef(getR32Match(73)), a: getWinnerOrRef(getR32Match(75)) },
          { idx: 2, id: "r16-91", mn: 91, h: getWinnerOrRef(getR32Match(76)), a: getWinnerOrRef(getR32Match(78)) },
          { idx: 3, id: "r16-92", mn: 92, h: getWinnerOrRef(getR32Match(79)), a: getWinnerOrRef(getR32Match(80)) },
          { idx: 4, id: "r16-93", mn: 93, h: getWinnerOrRef(getR32Match(83)), a: getWinnerOrRef(getR32Match(84)) },
          { idx: 5, id: "r16-94", mn: 94, h: getWinnerOrRef(getR32Match(81)), a: getWinnerOrRef(getR32Match(82)) },
          { idx: 6, id: "r16-95", mn: 95, h: getWinnerOrRef(getR32Match(88)), a: getWinnerOrRef(getR32Match(86)) },
          { idx: 7, id: "r16-96", mn: 96, h: getWinnerOrRef(getR32Match(85)), a: getWinnerOrRef(getR32Match(87)) },
        ];
        updated.roundOf16 = r16Data.map(d => ({
          ...updated.roundOf16?.[d.idx], id: d.id, matchNumber: d.mn, home: d.h, away: d.a,
          ...getOfficialOrReset(d.mn, updated.roundOf16?.[d.idx], d.h, d.a),
        }));
      }

      // Update Cuartos desde Octavos (con referencias GP)
      if (updated.roundOf16 && updated.roundOf16.length >= 8) {
        const getR16Match = (matchNum) => updated.roundOf16.find(m => m.matchNumber === matchNum);

        const r8Data = [
          { idx: 0, id: "r8-97", mn: 97, h: getWinnerOrRef(getR16Match(89)), a: getWinnerOrRef(getR16Match(90)) },
          { idx: 1, id: "r8-98", mn: 98, h: getWinnerOrRef(getR16Match(93)), a: getWinnerOrRef(getR16Match(94)) },
          { idx: 2, id: "r8-99", mn: 99, h: getWinnerOrRef(getR16Match(91)), a: getWinnerOrRef(getR16Match(92)) },
          { idx: 3, id: "r8-100", mn: 100, h: getWinnerOrRef(getR16Match(95)), a: getWinnerOrRef(getR16Match(96)) },
        ];
        updated.roundOf8 = r8Data.map(d => ({
          ...updated.roundOf8?.[d.idx], id: d.id, matchNumber: d.mn, home: d.h, away: d.a,
          ...getOfficialOrReset(d.mn, updated.roundOf8?.[d.idx], d.h, d.a),
        }));
      }

      // Update Semifinales desde Cuartos (con referencias GP)
      if (updated.roundOf8 && updated.roundOf8.length >= 4) {
        const getR8Match = (matchNum) => updated.roundOf8.find(m => m.matchNumber === matchNum);

        const sfData = [
          { idx: 0, id: "sf-101", mn: 101, h: getWinnerOrRef(getR8Match(97)), a: getWinnerOrRef(getR8Match(98)) },
          { idx: 1, id: "sf-102", mn: 102, h: getWinnerOrRef(getR8Match(99)), a: getWinnerOrRef(getR8Match(100)) },
        ];
        updated.semiFinals = sfData.map(d => ({
          ...updated.semiFinals?.[d.idx], id: d.id, matchNumber: d.mn, home: d.h, away: d.a,
          ...getOfficialOrReset(d.mn, updated.semiFinals?.[d.idx], d.h, d.a),
        }));
      }

      // Update Final y Tercer Lugar (con referencias GP/PP)
      if (updated.semiFinals && updated.semiFinals.length >= 2) {
        const getSFMatch = (matchNum) => updated.semiFinals.find(m => m.matchNumber === matchNum);

        const finalHome = getWinnerOrRef(getSFMatch(101));
        const finalAway = getWinnerOrRef(getSFMatch(102));
        updated.final = [{
          ...updated.final?.[0],
          id: "final-104",
          matchNumber: 104,
          home: finalHome,
          away: finalAway,
          ...getOfficialOrReset(104, updated.final?.[0], finalHome, finalAway),
        }];

        const thirdHome = getLoserOrRef(getSFMatch(101));
        const thirdAway = getLoserOrRef(getSFMatch(102));
        updated.thirdPlace = [{
          ...updated.thirdPlace?.[0],
          id: "third-103",
          matchNumber: 103,
          home: thirdHome,
          away: thirdAway,
          ...getOfficialOrReset(103, updated.thirdPlace?.[0], thirdHome, thirdAway),
        }];
      }

      return updated;
    });
  }, []);

  return (
    <WidgetContainer loading={loading} className="SimulatorWorldCupContainerGlobal">
      <div className="SimulatorWorldCupContainer">
        <Actions
          handlePhaseChange={handlePhaseChange}
          phase={phase}
          onSimulate={handleSimulate}
          onSimulateQualifying={handleSimulateQualifying}
          onReset={handleReset}
          isGroupsComplete={isGroupsComplete}
        />

        {phase === "groups" ? (
          <div className="SimulatorContent">
            <aside className="SimulatorLeftPanel">
              <GroupsPanel
                TABS={TABS}
                activeGroup={activeGroup}
                onSelect={setActiveGroup}
                groupsState={groupsState}
                standings={standings}
              />
            </aside>

            <main className="SimulatorMainPanel">
              <MatchesPanel
                TABS={TABS}
                groupId={activeGroup}
                group={groupsState[activeGroup]}
                standings={standings}
                onChangeScore={onChangeScore}
                onSelectGroup={setActiveGroup}
                onSimulateGroup={handleSimulateGroup}
                groupsState={groupsState}
              />
            </main>
          </div>
        ) : (
          <QualifyingRoundsPanel
            qualifyingState={qualifyingState}
            onSelectWinner={handleSelectWinner}
            scrollToChampion={scrollToChampion}
            onScrollComplete={() => setScrollToChampion(false)}
          />
        )}
      </div>
    </WidgetContainer>
  );
}